import React, { useState, useEffect } from "react";
import axios from "axios";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import LabelGenerator from "./components/LabelGenerator/LabelGenerator";
import LandingPage from "./components/LandingPage/LandingPage";
import HomePage from "./components/HomePage/HomePage";
import HomePage3PL from "./components/HomePage3PL/HomePage3PL";
import SetupIntegrationPartner from "./components/SetupIntegrationPartner/SetupIntegrationPartner";
import Login from "./components/Login/Login";
import Portal from "./components/Portal/Portal";
import SignUp from "./components/SignUp/SignUp";
import ForgotPassword from "./components/ForgotPassword/ForgotPassword";
import ResetPassword from "./components/ResetPassword/ResetPassword";
import ExchangeLink from "./components/ReturnsView/Exchanges/ExchangeLink";
import ReturnLink from "./components/ReturnsView/ReturnLink";
import SelectReturnOption from "./components/SelectReturnOption/SelectReturnOption";
import ConfirmationLinkSent from "./components/ConfirmationLinkSent/ConfirmationLinkSent";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute"; //ensures user is logged in or redirects to login.
import { FontProvider } from "./contexts/FontContext/FontContext";
import { OrdersProvider } from "./contexts/OrdersContext/OrdersContext";
import { LogoProvider } from "./contexts/LogoContext/LogoContext";
import useIdleLogout from "./hooks/useIdleLogout";
import "./App.css";
import "./styles/global.css";

function IdleLogoutWrapper({ children }) {
  const numMinutesBeforeLogout = 600; //10 hours of inactivity (in line with what's on server)
  useIdleLogout(numMinutesBeforeLogout * 60 * 1000); //ensures the hook runs inside Router
  return children;
}

function App() {
    const [csrfToken, setCsrfToken] = useState("");

    const getCsrfToken = async () => {
      const apiUrl = process.env.REACT_APP_API_URL; // Access environment-specific API URL
      try {
        const response = await axios.get(`${apiUrl}/api/csrf_token/`, { withCredentials: true }); // This ensures the CSRF token is stored as a cookie
        setCsrfToken(response.data.csrfToken);  // Store CSRF token in state
      } catch (error) { console.error("Error fetching CSRF token:", error); }
    };

    useEffect(() => { getCsrfToken(); }, []);
      
    return (
      <ErrorBoundary>
        <FontProvider>
            <Router>
                <IdleLogoutWrapper>
                  <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/signup" element={<SignUp csrfToken={csrfToken} />} />
                      <Route path="/setup_integration_partner" element={<ProtectedRoute><SetupIntegrationPartner csrfToken={csrfToken} /></ProtectedRoute>} />
                      <Route path="/home" element={<ProtectedRoute><OrdersProvider><HomePage /></OrdersProvider></ProtectedRoute>} />
                      <Route path="/home_3pl" element={<ProtectedRoute><HomePage3PL /></ProtectedRoute>} />
                      <Route path="/confirmation_link_sent" element={<ConfirmationLinkSent />} />
                      <Route path="/forgot_password" element={<ForgotPassword />} />
                      <Route path="/reset_password/:uid/:token" element={<ResetPassword />} />
                      <Route path="/returns/:order_id" element={<LogoProvider><ReturnLink isConfirmation={false} isExchange={false} /></LogoProvider>} />
                      <Route path="/returns_confirm/:order_id" element={<LogoProvider><ReturnLink isConfirmation={true} isExchange={false} /></LogoProvider>} />
                      <Route path="/exchanges_confirm/:order_id" element={<LogoProvider><ReturnLink isConfirmation={true} isExchange={true} /></LogoProvider>} />
                      <Route path="/exchanges/:order_id" element={<LogoProvider><ExchangeLink /></LogoProvider>} />
                      <Route path="/select_return_option/:order_id" element={<LogoProvider><SelectReturnOption /></LogoProvider>} />
                      <Route path="/label_generator" element={<ProtectedRoute><LabelGenerator /></ProtectedRoute>} />
                      <Route path="/:variable/portal" element={<Portal />} />
                      <Route path="/:variable/portal/returns" element={<Portal portalType="return" />} />
                      <Route path="/:variable/portal/exchanges" element={<Portal portalType="exchange" />} />
                  </Routes>
                </IdleLogoutWrapper>
            </Router>
        </FontProvider>
      </ErrorBoundary>
    );
}

export default App;
